import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleHelp, Send, Stamp } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { tasks, users } from '@/lib/api/endpoints';
import type { HandoverKind, TaskDetail } from '@/lib/api/types';
import { describeFailure } from '@/lib/i18n/errors';
import { useT, type MessageKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const KINDS: { kind: HandoverKind; icon: typeof Send; label: MessageKey; hint: MessageKey }[] = [
  { kind: 'DO', icon: Send, label: 'handover.kind.DO', hint: 'handover.hint.DO' },
  {
    kind: 'REVIEW',
    icon: CheckCircle2,
    label: 'handover.kind.REVIEW',
    hint: 'handover.hint.REVIEW',
  },
  { kind: 'APPROVE', icon: Stamp, label: 'handover.kind.APPROVE', hint: 'handover.hint.APPROVE' },
  {
    kind: 'QUESTION',
    icon: CircleHelp,
    label: 'handover.kind.QUESTION',
    hint: 'handover.hint.QUESTION',
  },
];

/**
 * Handing a task to somebody, as a request rather than a reassignment.
 *
 * Swapping a name answers *who* holds the task and nothing else — the
 * recipient still has to guess whether they are meant to do the work, check
 * it, decide on it, or just reply. So the form asks for three things and
 * refuses to send without the middle one:
 *
 *   1. what is being asked (four intents, because each ends differently),
 *   2. the what/where/when/how — required, and it lands in the comment thread
 *      where the recipient can answer it,
 *   3. by when, which is about *this request*, not the task's own deadline.
 */
export function HandoverDialog({
  task,
  currentUserId,
  onDone,
}: {
  task: TaskDetail;
  currentUserId: string;
  /** `close` asks the caller to shut the panel: the task is no longer ours. */
  onDone: (close?: boolean) => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<HandoverKind>('DO');
  const [assignee, setAssignee] = useState<string | null>(null);
  const [assigneeName, setAssigneeName] = useState('');
  const [note, setNote] = useState('');
  const [due, setDue] = useState('');

  const handOver = useMutation({
    mutationFn: () =>
      tasks.handOver(task.id, {
        assigned_to_user_id: assignee!,
        kind,
        note: note.trim(),
        due_date: due || null,
      }),
    onSuccess: (result) => {
      toast.show(
        t('handover.handedTo', { name: result.assigned_to_name }),
      );
      // Handing on a task you did not create takes it out of your own sight
      // — visibility is creator-or-assignee. Refetching the detail would run
      // straight into a 404 and leave an error box over a task that no
      // longer exists for you, so the server says which case this is and the
      // panel closes instead.
      onDone(!result.still_visible);
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (failure) => toast.error(describeFailure(failure, 'error.taskDelegate')),
  });

  const ready = Boolean(assignee) && note.trim().length >= 3;

  return (
    <div className="space-y-3 rounded-xl border border-border bg-surface p-3">
      <div>
        <span className="mb-1.5 block text-xs text-content-muted">
          {t('handover.what')}
        </span>
        <div className="flex flex-wrap gap-1.5">
          {KINDS.map((option) => (
            <button
              key={option.kind}
              type="button"
              onClick={() => setKind(option.kind)}
              aria-pressed={kind === option.kind}
              title={t(option.hint)}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-surface',
                kind === option.kind
                  ? 'bg-accent/10 font-medium text-accent'
                  : 'text-content-muted hover:bg-surface-hover hover:text-content',
              )}
            >
              <option.icon className="h-3.5 w-3.5" aria-hidden />
              {t(option.label)}
            </button>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-content-subtle">
          {t(KINDS.find((option) => option.kind === kind)!.hint)}
        </p>
      </div>

      <div>
        <span className="mb-1.5 block text-xs text-content-muted">
          {t('handover.who')}
        </span>
        <PersonPicker
          excludeUserId={currentUserId}
          selected={assignee}
          selectedName={assigneeName}
          onPick={(id, name) => {
            setAssignee(id);
            setAssigneeName(name);
          }}
        />
      </div>

      <label className="block">
        <span className="mb-1.5 block text-xs text-content-muted">
          {t('handover.note')}
        </span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          placeholder={t('handover.notePlaceholder')}
          className="w-full resize-y rounded-lg bg-surface px-3 py-2 text-sm text-content ring-1 ring-border placeholder:text-content-subtle focus:outline-none focus:ring-accent"
        />
      </label>

      <label className="block">
        <span className="mb-1.5 block text-xs text-content-muted">
          {t('handover.by')}
        </span>
        <input
          type="date"
          value={due}
          onChange={(event) => setDue(event.target.value)}
          className="rounded-lg bg-surface px-3 py-2 text-sm text-content ring-1 ring-border focus:outline-none focus:ring-accent"
        />
      </label>

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => onDone()}>
          {t('common.cancel')}
        </Button>
        <Button
          size="sm"
          disabled={!ready || handOver.isPending}
          onClick={() => handOver.mutate()}
        >
          {t('handover.send')}
        </Button>
      </div>
    </div>
  );
}

/** Search a teammate and hold the choice until the note is written. */
function PersonPicker({
  excludeUserId,
  selected,
  selectedName,
  onPick,
}: {
  /** Yourself. The search includes you by design; a handover to yourself is
      refused by the server, so offering it is offering an error. */
  excludeUserId: string;
  selected: string | null;
  selectedName: string;
  onPick: (id: string, name: string) => void;
}) {
  const t = useT();
  const [term, setTerm] = useState('');
  const { data: found } = useQuery({
    queryKey: ['users', 'search', term],
    queryFn: () => users.search(term),
    enabled: term.trim().length >= 2,
  });

  const candidates = (found ?? []).filter((person) => person.id !== excludeUserId);

  if (selected) {
    return (
      <button
        type="button"
        onClick={() => onPick('', '')}
        className="rounded-full bg-accent/10 px-2.5 py-1 text-xs font-medium text-accent"
      >
        {selectedName} ·
        <span className="ml-1 text-content-muted">{t('handover.change')}</span>
      </button>
    );
  }

  return (
    <>
      <input
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={t('handover.whoPlaceholder')}
        className="w-full rounded-lg bg-surface px-3 py-2 text-sm text-content ring-1 ring-border placeholder:text-content-subtle focus:outline-none focus:ring-accent"
      />
      {candidates.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {candidates.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                onClick={() => onPick(person.id, person.display_name)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-surface-hover"
              >
                <span className="min-w-0 flex-1 truncate text-content">
                  {person.display_name}
                  <span className="ml-1.5 text-xs text-content-subtle">
                    @{person.username}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/**
 * The request, shown on the task it was made about.
 *
 * The note is also a comment, but a thread scrolls and the ask is the reason
 * the task is on your list at all — it belongs at the top until it is
 * answered. Only the holder gets the button: answering is the one act that
 * closes it.
 */
export function HandoverBanner({
  task,
  currentUserId,
  onClose,
}: {
  task: TaskDetail;
  currentUserId: string;
  /** Called when answering has taken the task out of the viewer's sight. */
  onClose?: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [answer, setAnswer] = useState('');
  const [answering, setAnswering] = useState(false);

  const handBack = useMutation({
    mutationFn: () => tasks.handBack(task.id, answer.trim()),
    onSuccess: (result) => {
      toast.show(t('handover.returned', { name: result.returned_to_name }));
      setAnswering(false);
      setAnswer('');
      // Answering gives the task away, so the same rule as above.
      if (!result.still_visible) onClose?.();
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  if (!task.handover_kind) return null;

  const mine = task.assignee.id === currentUserId && task.status !== 'COMPLETED';
  const label = `handover.kind.${task.handover_kind}` as MessageKey;

  return (
    <section className="mb-3 rounded-xl border border-accent/30 bg-accent/5 p-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-xs font-medium text-accent">{t(label)}</span>
        {task.handover_by_name ? (
          <span className="text-xs text-content-muted">
            {t('handover.askedBy', { name: task.handover_by_name })}
          </span>
        ) : null}
        {task.handover_due_date ? (
          <span className="text-xs text-content-muted">
            · {t('handover.dueBy', { date: formatDay(task.handover_due_date) })}
          </span>
        ) : null}
      </div>

      {task.handover_note ? (
        <p className="mt-1.5 whitespace-pre-wrap text-sm text-content">
          {task.handover_note}
        </p>
      ) : null}

      {mine ? (
        answering ? (
          <div className="mt-2.5 space-y-2">
            <textarea
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              rows={2}
              autoFocus
              placeholder={t('handover.answerPlaceholder')}
              className="w-full resize-y rounded-lg bg-surface px-3 py-2 text-sm text-content ring-1 ring-border placeholder:text-content-subtle focus:outline-none focus:ring-accent"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setAnswering(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                disabled={answer.trim().length < 3 || handBack.isPending}
                onClick={() => handBack.mutate()}
              >
                {t('handover.handBack')}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            className="mt-2.5"
            onClick={() => setAnswering(true)}
          >
            {t('handover.answer')}
          </Button>
        )
      ) : null}
    </section>
  );
}

function formatDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day}. ${month}. ${year}`;
}
